/**
 * Приём номеров от кабинетов закупки.
 *
 * HTTP только кладёт сырьё и отвечает за 20 мс, разбор идёт батчами в воркере:
 * иначе кабинет ретраит по таймауту и удваивает и нагрузку, и расход (§6.8).
 * Тело вебхука валидируется мягко — payload сохраняется целиком в intake_events
 * даже когда формат неожиданный, иначе номер потерян, а деньги за него уплачены.
 */
import { z } from 'zod';
import { bigIdSchema, blockedReasonSchema, intakeDecisionSchema, isoDateTimeSchema, phoneE164Schema, refIdSchema, } from './common.dto.js';
/** Подпись вебхука: HMAC по сырому телу, окно 5 минут (§9.3) */
export const intakeHeadersSchema = z.object({
    'x-signature': z.string().min(16),
    'x-timestamp': z.string().min(1),
});
export const intakeParamsSchema = z.object({
    /** Длинный случайный сегмент пути для поставщиков, умеющих только URL */
    sourceSlug: z.string().min(3).max(120),
});
/**
 * Форма, в которой кабинеты присылают контакт. Все поля кроме телефона
 * необязательные: у каждого поставщика свой набор, и он меняется без предупреждения.
 */
export const intakePayloadSchema = z
    .object({
    phone: z.string().min(5).max(32),
    /** Дополнительные номера контакта: второй queue_item они не создают (§6.8) */
    phones: z.array(z.string().min(5).max(32)).max(10).optional(),
    /** Идентификатор заявки на стороне кабинета */
    vid: z.string().max(120).optional(),
    /** Метка проекта: по ней ищется источник */
    tag: z.string().max(200).optional(),
    project: z.string().max(200).optional(),
    name: z.string().max(200).optional(),
    region: z.string().max(120).optional(),
    /** Время на стороне поставщика: поле грязное, ключом ничего не является */
    time: z.string().max(64).optional(),
})
    .passthrough();
/** Ответ кабинету всегда короткий: длинный ответ провоцирует ретраи */
export const intakeAcceptedResponseSchema = z.object({ ok: z.literal(true) });
/** POST /api/intake/import — загрузка базы файлом из админки */
export const importCsvBodySchema = z.object({
    sourceId: refIdSchema,
    /** Цена контакта партии: снимок, пишется в batches */
    contactCost: z.string().regex(/^\d+(\.\d{1,4})?$/),
    /** Разделитель и кодировка задаются явно: автоопределение врёт на кириллице */
    delimiter: z.enum([',', ';', '\t']).default(';'),
    encoding: z.enum(['utf-8', 'windows-1251']).default('utf-8'),
});
/**
 * POST /api/sources/:id/import-text — тот же приём, но списком в теле запроса.
 *
 * Отдельный маршрут, а не второй формат тела у файлового: у файла параметры
 * едут в query (тело занято самим файлом), и смешивать два способа передать
 * одно и то же — гарантированное расхождение при первой правке.
 *
 * Номера разделяются переводом строки, запятой, точкой с запятой или пробелом
 * и принимаются в любом написании: разбор и нормализация — на сервере, теми же
 * функциями, что и у вебхука.
 */
export const importTextBodySchema = z.object({
    /** Верхняя граница — про размер тела, а не про правило: 2 МБ это ~150 тыс. номеров */
    text: z.string().min(1).max(2_000_000),
    contactCost: importCsvBodySchema.shape.contactCost,
});
/**
 * Адресная выдача: кому уходят принятые контакты, минуя общую раздачу.
 *
 * Отдельная схема, а не поле в теле загрузки: параметр понимают только те
 * маршруты, которые умеют его исполнить. Молча проигнорированный
 * `assignToDispatcherId` — худший из возможных исходов: ответ успешный,
 * номера в общем пуле, админ уверен, что диспетчер их получил.
 */
export const importHandoverSchema = z.object({
    assignToDispatcherId: bigIdSchema.optional(),
});
/** Счётчик строк: неотрицательное целое, одинаковый во всех разбивках ответа */
const countSchema = z.number().int().min(0);
/**
 * Разбивка приёма по решениям дедупа (§6.8) — НАСТОЯЩИЕ счётчики, а не оценка
 * по примерам ошибок. Ключи повторяют коды `intake_decision`, чтобы у отбитой
 * строки и у сводки было одно название: иначе «дубль» в списке и «повтор»
 * в итогах начнут считать разным.
 *
 * Тип `Record<IntakeDecision, …>` здесь несущий: новое решение приёма ломает
 * сборку в этом файле, а не тихо выпадает из сводки загрузки.
 */
const decisionCountShape = {
    /** Принят в очередь */
    queued: countSchema,
    /** Дубль: номер уже лежит в активной очереди */
    dup_active: countSchema,
    /** Дубль: по номеру уже был лид */
    dup_lead: countSchema,
    /** Дубль: звонили недавно, окно повторной закупки не вышло */
    dup_recent: countSchema,
    /** Чёрный список: отказ от обзвона */
    blocked_dnc: countSchema,
    /** Чёрный список: ранее был явный отказ */
    blocked_negative: countSchema,
    /** Не похоже на номер — единственная причина, за которую не заплачено */
    invalid_phone: countSchema,
};
export const intakeDecisionCountsSchema = z.object(decisionCountShape);
/** Кому и как ушли принятые контакты при адресной выдаче */
export const importHandoverResultSchema = z.object({
    dispatcherId: bigIdSchema,
    fullName: z.string(),
    /**
     * Берёт ли диспетчер работу прямо сейчас. false — контакты за ним закреплены,
     * но увидит он их, когда выйдет на линию.
     */
    onLine: z.boolean(),
    /** Почему не берёт: график, «готов», линия, лимит (§6.2) */
    blockedReason: blockedReasonSchema.nullable(),
});
export const importResultResponseSchema = z.object({
    importId: refIdSchema,
    rowsTotal: countSchema,
    rowsQueued: countSchema,
    rowsRejected: countSchema,
    /** Сколько строк и почему отбито — по первичке, а не по выборке примеров */
    byDecision: intakeDecisionCountsSchema,
    /** Первые несколько проблемных строк — чтобы человек понял, что не так с файлом */
    errorSample: z.array(z.object({ row: z.number().int().min(1), reason: z.string() })).max(20),
    /** null — адресной выдачи не просили */
    handover: importHandoverResultSchema.nullable(),
    /**
     * Сколько контактов диспетчер получил прямо сейчас, и сколько осталось ждать
     * в очереди закреплёнными за ним (дневной лимит выбран либо он не на линии).
     *
     * Полей нет вовсе, когда адресной выдачи не просили: «ноль» и «не спрашивали» —
     * разные вещи, и экран не должен рисовать блок закрепления для обычной загрузки.
     */
    assignedCnt: countSchema.optional(),
    heldByLimitCnt: countSchema.optional(),
});
/** GET /api/intake/deliveries — факт покупки, включая отбитые дубли. Это деньги */
export const deliveriesQuerySchema = z.object({
    sourceId: z.coerce.number().int().positive().optional(),
    decision: intakeDecisionSchema.optional(),
    phone: z.string().max(32).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
export const deliveryRowSchema = z.object({
    id: bigIdSchema,
    contactId: bigIdSchema,
    phone: phoneE164Schema,
    sourceId: refIdSchema,
    sourceTitle: z.string(),
    batchId: bigIdSchema,
    cost: z.string(),
    decision: intakeDecisionSchema,
    receivedAt: isoDateTimeSchema,
});
/** GET /api/intake/health — лаг разбора: больше 2 минут это авария (§6.8) */
export const intakeHealthResponseSchema = z.object({
    unparsedCnt: z.number().int().min(0),
    oldestUnparsedAgeSec: z.number().int().min(0),
    lastParsedAt: isoDateTimeSchema.nullable(),
});
//# sourceMappingURL=intake.dto.js.map